import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { credentialsApi } from '@/lib/api';
import { formatDate } from '@/utils/dateFormatter';
import { useViewportPageSize } from '@/hooks/use-viewport-page-size';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Key, Lock, Edit, Trash2, Search, X, ArrowUpDown, ArrowUp, ArrowDown, Share2, Users, Shield, Code, Database, Globe, Cloud, Server, FileText, Banknote, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { CredentialsPageSkeleton } from '@/components/ui/page-skeletons';
import { ShareCredentialDialog } from '@/components/credentials/ShareCredentialDialog';
import { ExportCredentialsDialog } from '@/components/credentials/ExportCredentialsDialog';
import { ImportCredentialsDialog } from '@/components/credentials/ImportCredentialsDialog';
import { CredentialDialog, CredentialIcon, MASKED_VALUE } from '@/components/credentials';
import { getCredentialTypeLabel } from '@/utils/credentialDefinitions';
import { 
  MicrosoftEntra as MicrosoftEntraIcon,
  MicrosoftSqlServer as SqlServerIcon,
  AzureBlobStorage as AzureBlobStorageIcon,
  MicrosoftTeams as MicrosoftTeamsIcon,
  MicrosoftOutlook as MicrosoftOutlookIcon,
  MicrosoftSharePoint as SharePointIcon,
  MicrosoftOneDrive as OneDriveIcon
} from '@/utils/microsoftIcons';
import {
  SiAnthropic,
  SiOpenai,
  SiPostgresql,
  SiMysql,
  SiMongodb,
  SiRedis,
  SiGooglecloud,
  SiAsana,
} from '@icons-pack/react-simple-icons';

interface Credential {
  id: string;
  name: string;
  type: string;
  created_at: string;
  updated_at: string;
  // Sharing fields
  isOwner?: boolean;
  isSharedWithMe?: boolean;
  sharedByDisplayName?: string;
  sharedByEmail?: string;
  sharedByInternalReferenceId?: string;
  shareCount?: number;
  sharedWithNames?: string[];
}

// Helper function to check if a credential type is Microsoft OAuth
function isMicrosoftOAuth(type: string): boolean {
  const microsoftOAuthTypes = [
    'microsoft_teams_oauth',
    'microsoft_teams_oauth2',
    'microsoft_outlook_oauth',
    'microsoft_outlook_oauth2',
    'microsoft_sharepoint_oauth',
    'microsoft_sharepoint_oauth2',
    'microsoft_onedrive_oauth',
    'microsoft_onedrive_oauth2',
    'microsoft_entra_oauth',
    'microsoft_entra_oauth2',
    'microsoft_excel_oauth2',
  ];
  return microsoftOAuthTypes.includes(type);
}

export default function Credentials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState<{ id: string; name: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; data?: any; details?: any } | null>(null);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [credentialToShare, setCredentialToShare] = useState<Credential | null>(null);

  // Export/Import dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  
  // Sorting states
  type SortField = 'name' | 'type' | 'created_at' | 'updated_at';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Tab state for owned vs shared credentials
  const [activeTab, setActiveTab] = useState<'owned' | 'shared'>('owned');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = useViewportPageSize({
    rowHeight: 54,      // Table row height (tuned to maximize rows without scrollbar)
    minPageSize: 5,
    maxPageSize: 50,
    bufferSpace: 45,    // 40px header row + 5px safety margin
    loading,            // Recalculate when loading completes
  });

  // Credential category/type mapping
  const [selectedCategory, setSelectedCategory] = useState<string>('general');

  const credentialCategories = {
    general: {
      label: 'General',
      types: [
        { value: 'api_key', label: 'API Key' },
        { value: 'bearer_token', label: 'Bearer Token' },
        { value: 'basic_auth', label: 'Basic Auth' },
        { value: 'oauth2', label: 'OAuth2' },
        { value: 'api_key_header', label: 'API Key Header' },
      ]
    },
    access: {
      label: 'Access Auth',
      types: [
        { value: 'data-engine', label: 'Data Engine' },
        { value: 'accessDocument', label: 'Access Document' },
        { value: 'access-charity-crm', label: 'Access Charity CRM (thankQ)' },
        { value: 'donorfy', label: 'Donorfy' },
        { value: 'paycircle', label: 'PayCircle' },
        { value: 'peoplehr', label: 'PeopleHR' },
      ]
    },
    ai: {
      label: 'AI Services',
      types: [
        { value: 'claude', label: 'Claude (Anthropic)' },
        { value: 'openai', label: 'OpenAI' },
        { value: 'azure-openai', label: 'Azure OpenAI' },
      ]
    },
    databases: {
      label: 'Databases',
      types: [
        { value: 'msSqlCredential', label: 'MS SQL Server' },
        { value: 'postgresql', label: 'PostgreSQL' },
        { value: 'mysql', label: 'MySQL' },
        { value: 'mongodb', label: 'MongoDB' },
        { value: 'turso', label: 'Turso' },
      ]
    },
    cloud: {
      label: 'Cloud Providers',
      types: [
        { value: 'aws', label: 'AWS' },
        { value: 'gcp', label: 'Google Cloud Platform' },
      ]
    },
    http: {
      label: 'HTTP',
      types: [
        { value: 'http_basic', label: 'HTTP Basic Auth' },
      ]
    },
    email: {
      label: 'Email',
      types: [
        { value: 'smtp', label: 'SMTP Email' },
      ]
    },
    fileTransfer: {
      label: 'File Transfer',
      types: [
        { value: 'ftp', label: 'FTP / SFTP' },
        { value: 'azure_blob_storage', label: 'Azure Blob Storage' },
      ]
    },
    integrations: {
      label: 'Integrations',
      types: [
        { value: 'microsoft_teams_oauth', label: 'Microsoft Teams (OAuth2)' },
        { value: 'microsoft_outlook_oauth', label: 'Microsoft Outlook (OAuth2)' },
        { value: 'microsoft_sharepoint_oauth', label: 'Microsoft SharePoint (OAuth2)' },
        { value: 'microsoft_onedrive_oauth', label: 'Microsoft OneDrive (OAuth2)' },
        { value: 'microsoft_entra_oauth', label: 'Microsoft Entra ID (OAuth2)' },
      ]
    },
  };

  // Get types for selected category
  const getTypesForCategory = (category: string) => {
    return credentialCategories[category as keyof typeof credentialCategories]?.types || [];
  };

  // Find category for a given type
  const getCategoryForType = (type: string): string => {
    for (const [category, data] of Object.entries(credentialCategories)) {
      if (data.types.some(t => t.value === type)) {
        return category;
      }
    }
    return 'general';
  };

  const [formData, setFormData] = useState<{
    name: string;
    type: string;
    value: string;
    username: string;
    password: string;
    clientId: string;
    clientSecret: string;
    authUrl: string;
    tokenUrl: string;
    headerName: string;
    host: string;
    port: string;
    database: string;
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
    projectId: string;
    secure: string;
    from: string;
    url: string;
    authToken: string;
    server: string;
    encrypt: boolean;
    domain: string;
    connectTimeout: string;
    requestTimeout: string;
    trustServerCertificate: boolean;
    tdsVersion: string;
    enableArithAbort: boolean;
    ftpData?: {
      protocol?: string;
      host?: string;
      port?: number;
      username?: string;
      password?: string;
      privateKey?: string;
      passphrase?: string;
      timeout?: number;
      ssl?: string;
      enableSsh?: boolean;
      sshMode?: string;
      passive?: boolean;
      authType?: string;
    };
    azureBlobStorageData?: {
      authMethod?: string;
      accountName?: string;
      accountKey?: string;
      connectionString?: string;
      sasToken?: string;
    };
    accessDocumentData?: {
      instanceId?: string;
      sharedSecret?: string;
      webServiceUrl?: string;
    };
    donorfyData?: {
      apiKey?: string;
      username?: string;
    };
    peopleHRData?: {
      apiKey?: string;
    };
    paycircleData?: {
      username?: string;
      password?: string;
      baseUrl?: string;
    };
    accessCharityCrmData?: {
      apiKey?: string;
      bearerToken?: string;
      baseUrl?: string;
    };
    // MySQL fields
    mySqlData?: {
      host?: string;
      database?: string;
      username?: string;
      password?: string;
      port?: string;
      sslMode?: string;
      connectTimeout?: string;
      allowUserVariables?: boolean;
      charset?: string;
    };
    // MongoDB fields
    mongoDBData?: {
      connectionString?: string;
      host?: string;
      port?: string;
      database?: string;
      username?: string;
      password?: string;
      authSource?: string;
      replicaSet?: string;
      ssl?: boolean;
    };
    // PostgreSQL fields
    postgreSqlData?: {
      host?: string;
      database?: string;
      username?: string;
      password?: string;
      port?: string;
      sslMode?: string;
      schema?: string;
      connectTimeout?: string;
      commandTimeout?: string;
      pooling?: boolean;
      minPoolSize?: string;
      maxPoolSize?: string;
    };
    tenantId?: string;
    scopes?: string;
    accessToken?: string;
    refreshToken?: string;
    tokenExpiresAt?: string;
    tokenType?: string;
    userEmail?: string;
    userName?: string;
    userId?: string;
    endpoint?: string; // For Azure OpenAI
  }>({
    name: '',
    type: 'api_key',
    value: '',
    username: '',
    password: '',
    clientId: '',
    clientSecret: '',
    authUrl: '',
    tokenUrl: '',
    headerName: '',
    host: '',
    port: '',
    database: '',
    region: '',
    accessKeyId: '',
    secretAccessKey: '',
    projectId: '',
    secure: 'false',
    from: '',
    url: '',
    authToken: '',
    // MS SQL Advanced fields
    server: '',
    encrypt: true,
    domain: '',
    connectTimeout: '',
    requestTimeout: '',
    trustServerCertificate: false,
    tdsVersion: '',
    enableArithAbort: true,
    // FTP fields
    ftpData: undefined,
    // Azure Blob Storage fields
    azureBlobStorageData: undefined,
    // Access Document fields
    accessDocumentData: undefined,
    // Donorfy fields
    donorfyData: undefined,
    // PeopleHR fields
    peopleHRData: undefined,
    // PayCircle fields
    paycircleData: undefined,
    // Access Charity CRM fields
    accessCharityCrmData: undefined,
    // MySQL fields
    mySqlData: undefined,
    // MongoDB fields
    mongoDBData: undefined,
    // PostgreSQL fields
    postgreSqlData: undefined,
  });

  // Debounce search input
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchQuery(value);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchQuery);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchQuery, debouncedSetSearch]);

  useEffect(() => {
    loadCredentials();
  }, []);

  // Handle ?create=true query parameter to open create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setShowDialog(true);
      // Remove the query parameter from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadCredentials = async () => {
    try {
      const response = await credentialsApi.list();
      // Handle different response structures
      if (response.data) {
        if (Array.isArray(response.data)) {
          setCredentials(response.data);
        } else if (response.data.credentials && Array.isArray(response.data.credentials)) {
          setCredentials(response.data.credentials);
        } else {
          setCredentials([]);
        }
      } else {
        setCredentials([]);
      }
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load credentials');
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload: any = {
        name: formData.name,
        type: formData.type,
      };

      switch (formData.type) {
        case 'api_key':
        case 'bearer_token':
        case 'claude':
        case 'openai':
        case 'data-engine':
          payload.value = formData.value;
          break;
        case 'azure-openai':
          if (!formData.endpoint) {
            toast.error('Please enter Azure OpenAI endpoint');
            return;
          }
          if (!formData.value) {
            toast.error('Please enter Azure OpenAI API key');
            return;
          }
          payload.endpoint = formData.endpoint;
          payload.apiKey = formData.value;
          break;
        case 'basic_auth':
        case 'http_basic':
          payload.username = formData.username;
          payload.password = formData.password;
          break;
        case 'oauth2':
        case 'azure':
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          if (formData.type === 'oauth2') {
            payload.authUrl = formData.authUrl;
            payload.tokenUrl = formData.tokenUrl;
          } else {
            payload.tenantId = formData.value;
          }
          break;
        case 'api_key_header':
          payload.headerName = formData.headerName;
          payload.value = formData.value;
          break;
        case 'msSqlCredential':
          payload.server = formData.server || formData.host; // Support both server and host
          payload.port = formData.port;
          payload.database = formData.database;
          payload.username = formData.username;
          payload.password = formData.password;
          payload.encrypt = formData.encrypt;
          // Advanced fields
          if (formData.domain) payload.domain = formData.domain;
          if (formData.connectTimeout) payload.connectTimeout = parseInt(formData.connectTimeout);
          if (formData.requestTimeout) payload.requestTimeout = parseInt(formData.requestTimeout);
          payload.trustServerCertificate = formData.trustServerCertificate;
          if (formData.tdsVersion) payload.tdsVersion = formData.tdsVersion;
          payload.enableArithAbort = formData.enableArithAbort;
          break;
        case 'postgresql':
          payload.host = formData.postgreSqlData?.host || formData.host;
          payload.port = formData.postgreSqlData?.port || formData.port || '5432';
          payload.database = formData.postgreSqlData?.database || formData.database;
          payload.username = formData.postgreSqlData?.username || formData.username;
          payload.password = formData.postgreSqlData?.password || formData.password;
          payload.sslMode = formData.postgreSqlData?.sslMode || 'Prefer';
          payload.schema = formData.postgreSqlData?.schema || 'public';
          break;
        case 'mysql':
          payload.host = formData.mySqlData?.host || formData.host;
          payload.port = formData.mySqlData?.port || formData.port || '3306';
          payload.database = formData.mySqlData?.database || formData.database;
          payload.username = formData.mySqlData?.username || formData.username;
          payload.password = formData.mySqlData?.password || formData.password;
          payload.sslMode = formData.mySqlData?.sslMode || 'Preferred';
          break;
        case 'mongodb':
          // Support both connection string and individual fields
          if (formData.mongoDBData?.connectionString) {
            payload.connectionString = formData.mongoDBData.connectionString;
          } else {
            payload.host = formData.mongoDBData?.host || formData.host;
            payload.port = formData.mongoDBData?.port || formData.port || '27017';
            payload.database = formData.mongoDBData?.database || formData.database;
            payload.username = formData.mongoDBData?.username || formData.username;
            payload.password = formData.mongoDBData?.password || formData.password;
            payload.authSource = formData.mongoDBData?.authSource || 'admin';
            payload.ssl = formData.mongoDBData?.ssl;
          }
          break;
        case 'turso':
          if (!formData.url || !formData.authToken || formData.authToken === MASKED_VALUE) {
            toast.error('Please enter a valid Turso database URL and auth token');
            return;
          }
          payload.url = formData.url;
          payload.authToken = formData.authToken;
          break;
        case 'aws':
          payload.accessKeyId = formData.accessKeyId;
          payload.secretAccessKey = formData.secretAccessKey;
          payload.region = formData.region;
          break;
        case 'gcp':
          payload.projectId = formData.projectId;
          payload.serviceAccountKey = formData.value;
          break;
        case 'smtp':
          payload.host = formData.host;
          payload.port = formData.port || '587';
          payload.secure = formData.secure === 'true';
          payload.user = formData.username;
          payload.password = formData.password;
          break;
        case 'ftp':
          if (!formData.ftpData?.host) {
            toast.error('Please enter FTP host');
            return;
          }
          // Only require username if not anonymous
          const ftpAuthType = formData.ftpData?.authType || 'credentials';
          if (ftpAuthType !== 'anonymous' && !formData.ftpData?.username) {
            toast.error('Please enter FTP username');
            return;
          }
          const ftpProtocol = formData.ftpData?.protocol || 'ftp';
          payload.protocol = ftpProtocol;
          payload.host = formData.ftpData.host;
          // Port must be sent as a string to match backend expectations
          payload.port = String(formData.ftpData.port || (ftpProtocol === 'sftp' ? 22 : 21));
          payload.username = formData.ftpData.username;
          payload.password = formData.ftpData.password;
          payload.privateKey = formData.ftpData.privateKey;
          payload.passphrase = formData.ftpData.passphrase;
          // Timeout is expected as an integer in the backend
          payload.timeout = formData.ftpData.timeout || 60000;
          // Always send defaults for optional fields so they get saved
          payload.ssl = formData.ftpData.ssl || 'none';
          payload.enableSsh = formData.ftpData.enableSsh ?? (ftpProtocol === 'sftp');
          payload.sshMode = formData.ftpData.sshMode || 'password';
          payload.passive = formData.ftpData.passive ?? true;
          payload.authType = ftpAuthType;
          break;
        case 'azure_blob_storage':
          const azureAuthMethod = formData.azureBlobStorageData?.authMethod || 'storageKey';
          if (azureAuthMethod === 'storageKey') {
            if (!formData.azureBlobStorageData?.accountName) {
              toast.error('Please enter Storage Account Name');
              return;
            }
            if (!formData.azureBlobStorageData?.accountKey) {
              toast.error('Please enter Account Key');
              return;
            }
          } else if (azureAuthMethod === 'connectionString') {
            if (!formData.azureBlobStorageData?.connectionString) {
              toast.error('Please enter Connection String');
              return;
            }
          } else if (azureAuthMethod === 'sasToken') {
            if (!formData.azureBlobStorageData?.accountName) {
              toast.error('Please enter Storage Account Name');
              return;
            }
            if (!formData.azureBlobStorageData?.sasToken) {
              toast.error('Please enter SAS Token');
              return;
            }
          }
          payload.authMethod = azureAuthMethod;
          payload.accountName = formData.azureBlobStorageData?.accountName || '';
          payload.accountKey = formData.azureBlobStorageData?.accountKey || '';
          payload.connectionString = formData.azureBlobStorageData?.connectionString || '';
          payload.sasToken = formData.azureBlobStorageData?.sasToken || '';
          break;
        case 'accessDocument':
          if (!formData.accessDocumentData?.instanceId) {
            toast.error('Please enter Instance ID');
            return;
          }
          if (!formData.accessDocumentData?.sharedSecret) {
            toast.error('Please enter Shared Secret');
            return;
          }
          payload.instanceId = formData.accessDocumentData.instanceId;
          payload.sharedSecret = formData.accessDocumentData.sharedSecret;
          payload.webServiceUrl = formData.accessDocumentData.webServiceUrl || '';
          break;
        case 'donorfy':
          if (!formData.donorfyData?.apiKey) {
            toast.error('Please enter API Key');
            return;
          }
          payload.apiKey = formData.donorfyData.apiKey;
          payload.username = formData.donorfyData.username || 'EvoWorkflow';
          break;
        case 'peoplehr':
          if (!formData.peopleHRData?.apiKey) {
            toast.error('Please enter API Key');
            return;
          }
          payload.apiKey = formData.peopleHRData.apiKey;
          break;
        case 'paycircle':
          if (!formData.paycircleData?.username) {
            toast.error('Please enter Username');
            return;
          }
          if (!formData.paycircleData?.password) {
            toast.error('Please enter Password');
            return;
          }
          payload.username = formData.paycircleData.username;
          payload.password = formData.paycircleData.password;
          payload.baseUrl = formData.paycircleData.baseUrl || '';
          break;
        case 'access-charity-crm':
          if (!formData.accessCharityCrmData?.apiKey && !formData.accessCharityCrmData?.bearerToken) {
            toast.error('Please enter at least one of: API Key or Bearer Token');
            return;
          }
          payload.apiKey = formData.accessCharityCrmData.apiKey || '';
          payload.bearerToken = formData.accessCharityCrmData.bearerToken || '';
          payload.baseUrl = formData.accessCharityCrmData.baseUrl || '';
          break;
        case 'microsoft_teams_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          break;
        case 'microsoft_outlook_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          payload.scopes = formData.scopes || 'offline_access,User.Read,Mail.ReadWrite,Mail.Send,Calendars.ReadWrite,Contacts.ReadWrite,MailboxSettings.ReadWrite';
          break;
        case 'microsoft_sharepoint_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          payload.scopes = formData.scopes || 'offline_access,Sites.Read.All,Sites.ReadWrite.All';
          break;
        case 'microsoft_onedrive_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          payload.scopes = formData.scopes || 'offline_access,Files.ReadWrite.All';
          break;
        case 'microsoft_entra_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          payload.clientSecret = formData.clientSecret;
          payload.scopes = formData.scopes || 'offline_access,User.Read.All,Group.Read.All,GroupMember.Read.All';
          break;
      }

      console.log('[Credentials] Creating credential with payload:', payload);
      await credentialsApi.create(payload);
      toast.success('Credential created successfully');
      setShowDialog(false);
      resetForm();
      loadCredentials();
    } catch (error: any) {
      console.error('[Credentials] Failed to create credential:', error);
      console.error('[Credentials] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create credential';
      toast.error(errorMessage);
    }
  };

  const handleUpdate = async () => {
    if (!editingCredential) return;
    try {
      const payload: any = {
        name: formData.name,
        type: formData.type,
      };

      switch (formData.type) {
        case 'api_key':
        case 'claude':
        case 'openai':
        case 'data-engine':
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
          break;
        case 'azure-openai':
          payload.endpoint = formData.endpoint; // Endpoint is not sensitive, always send it
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.apiKey = formData.value;
          }
          break;
        case 'bearer_token':
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
          break;
        case 'basic_auth':
        case 'http_basic':
          payload.username = formData.username; // Username is not sensitive, always send it
          if (formData.password && formData.password !== MASKED_VALUE) {
            payload.password = formData.password;
          }
          break;
        case 'oauth2':
          if (formData.clientId && formData.clientId !== MASKED_VALUE) {
            payload.clientId = formData.clientId;
          }
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          payload.authUrl = formData.authUrl;
          payload.tokenUrl = formData.tokenUrl;
          break;
        case 'api_key_header':
          payload.headerName = formData.headerName;
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.value = formData.value;
          }
          break;
        case 'msSqlCredential':
          payload.server = formData.server || formData.host; // Support both server and host
          payload.port = formData.port;
          payload.database = formData.database;
          payload.username = formData.username; // Username is not sensitive, always send it
          if (formData.password && formData.password !== MASKED_VALUE) {
            payload.password = formData.password;
          }
          payload.encrypt = formData.encrypt;
          // Advanced fields
          if (formData.domain) payload.domain = formData.domain;
          if (formData.connectTimeout) payload.connectTimeout = parseInt(formData.connectTimeout);
          if (formData.requestTimeout) payload.requestTimeout = parseInt(formData.requestTimeout);
          payload.trustServerCertificate = formData.trustServerCertificate;
          if (formData.tdsVersion) payload.tdsVersion = formData.tdsVersion;
          payload.enableArithAbort = formData.enableArithAbort;
          break;
        case 'postgresql':
          payload.host = formData.postgreSqlData?.host || formData.host;
          payload.port = formData.postgreSqlData?.port || formData.port || '5432';
          payload.database = formData.postgreSqlData?.database || formData.database;
          payload.username = formData.postgreSqlData?.username || formData.username;
          payload.sslMode = formData.postgreSqlData?.sslMode || 'Prefer';
          payload.schema = formData.postgreSqlData?.schema || 'public';
          if ((formData.postgreSqlData?.password || formData.password) &&
              (formData.postgreSqlData?.password || formData.password) !== MASKED_VALUE) {
            payload.password = formData.postgreSqlData?.password || formData.password;
          }
          break;
        case 'mysql':
          payload.host = formData.mySqlData?.host || formData.host;
          payload.port = formData.mySqlData?.port || formData.port || '3306';
          payload.database = formData.mySqlData?.database || formData.database;
          payload.username = formData.mySqlData?.username || formData.username;
          payload.sslMode = formData.mySqlData?.sslMode || 'Preferred';
          if ((formData.mySqlData?.password || formData.password) &&
              (formData.mySqlData?.password || formData.password) !== MASKED_VALUE) {
            payload.password = formData.mySqlData?.password || formData.password;
          }
          break;
        case 'mongodb':
          // Support both connection string and individual fields
          if (formData.mongoDBData?.connectionString) {
            payload.connectionString = formData.mongoDBData.connectionString;
          } else {
            payload.host = formData.mongoDBData?.host || formData.host;
            payload.port = formData.mongoDBData?.port || formData.port || '27017';
            payload.database = formData.mongoDBData?.database || formData.database;
            payload.authSource = formData.mongoDBData?.authSource || 'admin';
            payload.ssl = formData.mongoDBData?.ssl;
            payload.username = formData.mongoDBData?.username || formData.username;
            if ((formData.mongoDBData?.password || formData.password) &&
                (formData.mongoDBData?.password || formData.password) !== MASKED_VALUE) {
              payload.password = formData.mongoDBData?.password || formData.password;
            }
          }
          break;
        case 'turso':
          if (!formData.url) {
            toast.error('Please enter a valid Turso database URL');
            return;
          }
          payload.url = formData.url;
          if (formData.authToken && formData.authToken !== MASKED_VALUE) {
            payload.authToken = formData.authToken;
          }
          break;
        case 'aws':
          payload.accessKeyId = formData.accessKeyId;
          if (formData.secretAccessKey && formData.secretAccessKey !== MASKED_VALUE) {
            payload.secretAccessKey = formData.secretAccessKey;
          }
          payload.region = formData.region;
          break;
        case 'azure':
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.tenantId = formData.value;
          }
          break;
        case 'gcp':
          payload.projectId = formData.projectId;
          if (formData.value && formData.value !== MASKED_VALUE) {
            payload.serviceAccountKey = formData.value;
          }
          break;
        case 'smtp':
          payload.host = formData.host;
          payload.port = formData.port || '587';
          payload.secure = formData.secure === 'true';
          payload.user = formData.username;
          if (formData.password && formData.password !== MASKED_VALUE) {
            payload.password = formData.password;
          }
          break;
        case 'ftp':
          const updateFtpProtocol = formData.ftpData?.protocol || 'ftp';
          const updateFtpAuthType = formData.ftpData?.authType || 'credentials';
          payload.protocol = updateFtpProtocol;
          payload.host = formData.ftpData?.host;
          // Port must be sent as a string to match backend expectations
          payload.port = String(formData.ftpData?.port || (updateFtpProtocol === 'sftp' ? 22 : 21));
          payload.username = formData.ftpData?.username;
          if (formData.ftpData?.password && formData.ftpData.password !== MASKED_VALUE) {
            payload.password = formData.ftpData.password;
          }
          if (formData.ftpData?.privateKey && formData.ftpData.privateKey !== MASKED_VALUE) {
            payload.privateKey = formData.ftpData.privateKey;
          }
          if (formData.ftpData?.passphrase && formData.ftpData.passphrase !== MASKED_VALUE) {
            payload.passphrase = formData.ftpData.passphrase;
          }
          payload.timeout = formData.ftpData?.timeout || 60000;
          // Always send these fields so they get saved (use defaults if not set)
          payload.ssl = formData.ftpData?.ssl || 'none';
          payload.enableSsh = formData.ftpData?.enableSsh ?? (updateFtpProtocol === 'sftp');
          payload.sshMode = formData.ftpData?.sshMode || 'password';
          payload.passive = formData.ftpData?.passive ?? true;
          payload.authType = updateFtpAuthType;
          break;
        case 'azure_blob_storage':
          payload.authMethod = formData.azureBlobStorageData?.authMethod || 'storageKey';
          payload.accountName = formData.azureBlobStorageData?.accountName || '';
          if (formData.azureBlobStorageData?.accountKey && formData.azureBlobStorageData.accountKey !== MASKED_VALUE) {
            payload.accountKey = formData.azureBlobStorageData.accountKey;
          }
          if (formData.azureBlobStorageData?.connectionString && formData.azureBlobStorageData.connectionString !== MASKED_VALUE) {
            payload.connectionString = formData.azureBlobStorageData.connectionString;
          }
          if (formData.azureBlobStorageData?.sasToken && formData.azureBlobStorageData.sasToken !== MASKED_VALUE) {
            payload.sasToken = formData.azureBlobStorageData.sasToken;
          }
          break;
        case 'accessDocument':
          payload.instanceId = formData.accessDocumentData?.instanceId;
          if (formData.accessDocumentData?.sharedSecret && formData.accessDocumentData.sharedSecret !== MASKED_VALUE) {
            payload.sharedSecret = formData.accessDocumentData.sharedSecret;
          }
          payload.webServiceUrl = formData.accessDocumentData?.webServiceUrl || '';
          break;
        case 'donorfy':
          payload.username = formData.donorfyData?.username || 'EvoWorkflow';
          if (formData.donorfyData?.apiKey && formData.donorfyData.apiKey !== MASKED_VALUE) {
            payload.apiKey = formData.donorfyData.apiKey;
          }
          break;
        case 'peoplehr':
          if (formData.peopleHRData?.apiKey && formData.peopleHRData.apiKey !== MASKED_VALUE) {
            payload.apiKey = formData.peopleHRData.apiKey;
          }
          break;
        case 'paycircle':
          if (formData.paycircleData?.username && formData.paycircleData.username !== MASKED_VALUE) {
            payload.username = formData.paycircleData.username;
          }
          if (formData.paycircleData?.password && formData.paycircleData.password !== MASKED_VALUE) {
            payload.password = formData.paycircleData.password;
          }
          payload.baseUrl = formData.paycircleData?.baseUrl || '';
          break;
        case 'access-charity-crm':
          if (formData.accessCharityCrmData?.apiKey && formData.accessCharityCrmData.apiKey !== MASKED_VALUE) {
            payload.apiKey = formData.accessCharityCrmData.apiKey;
          }
          if (formData.accessCharityCrmData?.bearerToken && formData.accessCharityCrmData.bearerToken !== MASKED_VALUE) {
            payload.bearerToken = formData.accessCharityCrmData.bearerToken;
          }
          payload.baseUrl = formData.accessCharityCrmData?.baseUrl || '';
          break;
        case 'microsoft_teams_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          // Preserve OAuth tokens if they exist
          if (formData.accessToken) payload.accessToken = formData.accessToken;
          if (formData.refreshToken) payload.refreshToken = formData.refreshToken;
          if (formData.tokenExpiresAt) payload.tokenExpiresAt = formData.tokenExpiresAt;
          if (formData.tokenType) payload.tokenType = formData.tokenType;
          if (formData.userEmail) payload.userEmail = formData.userEmail;
          if (formData.userName) payload.userName = formData.userName;
          if (formData.userId) payload.userId = formData.userId;
          break;
        case 'microsoft_outlook_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          payload.scopes = formData.scopes || 'offline_access,User.Read,Mail.ReadWrite,Mail.Send,Calendars.ReadWrite,Contacts.ReadWrite,MailboxSettings.ReadWrite';
          // Preserve OAuth tokens if they exist
          if (formData.accessToken) payload.accessToken = formData.accessToken;
          if (formData.refreshToken) payload.refreshToken = formData.refreshToken;
          if (formData.tokenExpiresAt) payload.tokenExpiresAt = formData.tokenExpiresAt;
          if (formData.tokenType) payload.tokenType = formData.tokenType;
          if (formData.userEmail) payload.userEmail = formData.userEmail;
          if (formData.userName) payload.userName = formData.userName;
          if (formData.userId) payload.userId = formData.userId;
          break;
        case 'microsoft_sharepoint_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          payload.scopes = formData.scopes || 'offline_access,Sites.Read.All,Sites.ReadWrite.All';
          // Preserve OAuth tokens if they exist
          if (formData.accessToken) payload.accessToken = formData.accessToken;
          if (formData.refreshToken) payload.refreshToken = formData.refreshToken;
          if (formData.tokenExpiresAt) payload.tokenExpiresAt = formData.tokenExpiresAt;
          if (formData.tokenType) payload.tokenType = formData.tokenType;
          if (formData.userEmail) payload.userEmail = formData.userEmail;
          if (formData.userName) payload.userName = formData.userName;
          if (formData.userId) payload.userId = formData.userId;
          break;
        case 'microsoft_onedrive_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== MASKED_VALUE) {
            payload.clientSecret = formData.clientSecret;
          }
          payload.scopes = formData.scopes || 'offline_access,Files.ReadWrite.All';
          // Preserve OAuth tokens if they exist
          if (formData.accessToken) payload.accessToken = formData.accessToken;
          if (formData.refreshToken) payload.refreshToken = formData.refreshToken;
          if (formData.tokenExpiresAt) payload.tokenExpiresAt = formData.tokenExpiresAt;
          if (formData.tokenType) payload.tokenType = formData.tokenType;
          if (formData.userEmail) payload.userEmail = formData.userEmail;
          if (formData.userName) payload.userName = formData.userName;
          if (formData.userId) payload.userId = formData.userId;
          break;
        case 'microsoft_entra_oauth':
          payload.tenantId = formData.tenantId;
          payload.clientId = formData.clientId;
          if (formData.clientSecret && formData.clientSecret !== '••••••••') {
            payload.clientSecret = formData.clientSecret;
          }
          payload.scopes = formData.scopes || 'offline_access,User.Read.All,Group.Read.All,GroupMember.Read.All';
          // Preserve OAuth tokens if they exist
          if (formData.accessToken) payload.accessToken = formData.accessToken;
          if (formData.refreshToken) payload.refreshToken = formData.refreshToken;
          if (formData.tokenExpiresAt) payload.tokenExpiresAt = formData.tokenExpiresAt;
          if (formData.tokenType) payload.tokenType = formData.tokenType;
          if (formData.userEmail) payload.userEmail = formData.userEmail;
          if (formData.userName) payload.userName = formData.userName;
          if (formData.userId) payload.userId = formData.userId;
          break;
      }

      await credentialsApi.update(editingCredential.id, payload);
      toast.success('Credential updated successfully');
      setEditingCredential(null);
      setShowDialog(false);
      resetForm();
      loadCredentials();
    } catch (error: any) {
      console.error('Update credential error:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Failed to update credential';
      toast.error(errorMessage);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setCredentialToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCredential = async () => {
    if (!credentialToDelete) return;
    try {
      await credentialsApi.delete(credentialToDelete.id);
      toast.success('Credential deleted successfully');
      setDeleteDialogOpen(false);
      setCredentialToDelete(null);
      loadCredentials();
    } catch (error) {
      toast.error('Failed to delete credential');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // If editing an existing credential, fetch the actual data to merge with form data
      let actualCredentialData: any = {};
      if (editingCredential) {
        try {
          const response = await credentialsApi.get(editingCredential.id);
          actualCredentialData = response.data.data || {};
        } catch (error) {
          console.error('Failed to fetch credential data for testing:', error);
        }
      }

      // Helper function to get the actual value (use form value if changed, otherwise use actual value)
      const getActualValue = (formValue: string, actualValue: string) => {
        if (formValue && formValue !== MASKED_VALUE) {
          return formValue;
        }
        return actualValue;
      };

      // Build the test payload from current form data
      const testPayload: any = {
        type: formData.type,
      };

      switch (formData.type) {
        case 'api_key':
        case 'bearer_token':
        case 'claude':
        case 'openai':
        case 'data-engine':
          testPayload.data = {
            value: getActualValue(formData.value, actualCredentialData.value)
          };
          break;
        case 'basic_auth':
        case 'http_basic':
          testPayload.data = {
            username: formData.username,
            password: getActualValue(formData.password, actualCredentialData.password),
          };
          break;
        case 'oauth2':
          testPayload.data = {
            clientId: getActualValue(formData.clientId, actualCredentialData.clientId),
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
            authUrl: formData.authUrl,
            tokenUrl: formData.tokenUrl,
          };
          break;
        case 'api_key_header':
          testPayload.data = {
            headerName: formData.headerName,
            value: getActualValue(formData.value, actualCredentialData.value),
          };
          break;
        case 'msSqlCredential':
          testPayload.data = {
            server: formData.server || formData.host,
            port: formData.port,
            database: formData.database,
            username: formData.username,
            password: getActualValue(formData.password, actualCredentialData.password),
            encrypt: formData.encrypt,
            // Advanced fields
            domain: formData.domain,
            connectTimeout: formData.connectTimeout ? parseInt(formData.connectTimeout) : undefined,
            requestTimeout: formData.requestTimeout ? parseInt(formData.requestTimeout) : undefined,
            trustServerCertificate: formData.trustServerCertificate,
            tdsVersion: formData.tdsVersion,
            enableArithAbort: formData.enableArithAbort,
          };
          break;
        case 'postgresql':
          testPayload.data = {
            host: formData.postgreSqlData?.host || formData.host,
            port: formData.postgreSqlData?.port || formData.port || '5432',
            database: formData.postgreSqlData?.database || formData.database,
            username: formData.postgreSqlData?.username || formData.username,
            password: getActualValue(formData.postgreSqlData?.password || formData.password, actualCredentialData.password),
            sslMode: formData.postgreSqlData?.sslMode || 'Prefer',
            schema: formData.postgreSqlData?.schema || 'public',
          };
          break;
        case 'mysql':
          testPayload.data = {
            host: formData.mySqlData?.host || formData.host,
            port: formData.mySqlData?.port || formData.port || '3306',
            database: formData.mySqlData?.database || formData.database,
            username: formData.mySqlData?.username || formData.username,
            password: getActualValue(formData.mySqlData?.password || formData.password, actualCredentialData.password),
            sslMode: formData.mySqlData?.sslMode || 'Preferred',
          };
          break;
        case 'mongodb':
          // Support both connection string and individual fields
          if (formData.mongoDBData?.connectionString) {
            testPayload.data = {
              connectionString: formData.mongoDBData.connectionString,
            };
          } else {
            testPayload.data = {
              host: formData.mongoDBData?.host || formData.host,
              port: formData.mongoDBData?.port || formData.port || '27017',
              database: formData.mongoDBData?.database || formData.database,
              username: formData.mongoDBData?.username || formData.username,
              password: getActualValue(formData.mongoDBData?.password || formData.password, actualCredentialData.password),
              authSource: formData.mongoDBData?.authSource || 'admin',
              ssl: formData.mongoDBData?.ssl,
            };
          }
          break;
        case 'turso':
          const actualAuthToken = getActualValue(formData.authToken, actualCredentialData.authToken);
          if (!formData.url || !actualAuthToken) {
            toast.error('Please enter a valid Turso database URL and auth token');
            setTesting(false);
            return;
          }
          testPayload.data = {
            url: formData.url,
            authToken: actualAuthToken,
          };
          break;
        case 'aws':
          testPayload.data = {
            accessKeyId: formData.accessKeyId,
            secretAccessKey: getActualValue(formData.secretAccessKey, actualCredentialData.secretAccessKey),
            region: formData.region,
          };
          break;
        case 'azure':
          testPayload.data = {
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
            tenantId: getActualValue(formData.value, actualCredentialData.tenantId),
          };
          break;
        case 'gcp':
          testPayload.data = {
            projectId: formData.projectId,
            serviceAccountKey: getActualValue(formData.value, actualCredentialData.serviceAccountKey),
          };
          break;
        case 'smtp':
          testPayload.data = {
            host: formData.host,
            port: formData.port || '587',
            secure: formData.secure === 'true',
            user: formData.username,
            password: getActualValue(formData.password, actualCredentialData.password),
          };
          break;
        case 'ftp':
          if (!formData.ftpData?.host) {
            toast.error('Please enter FTP host');
            setTesting(false);
            return;
          }
          testPayload.data = {
            protocol: formData.ftpData.protocol || 'ftp',
            host: formData.ftpData.host,
            port: String(formData.ftpData.port || (formData.ftpData.protocol === 'sftp' ? 22 : 21)),
            username: formData.ftpData.username,
            password: getActualValue(formData.ftpData.password, actualCredentialData?.password),
            privateKey: formData.ftpData.privateKey,
            passphrase: formData.ftpData.passphrase,
            timeout: formData.ftpData.timeout || 60000,
            ssl: formData.ftpData.ssl,
            enableSsh: formData.ftpData.enableSsh,
            sshMode: formData.ftpData.sshMode,
            passive: formData.ftpData.passive,
            authType: formData.ftpData.authType,
          };
          break;
        case 'azure_blob_storage':
          const testAzureAuthMethod = formData.azureBlobStorageData?.authMethod || 'storageKey';
          if (testAzureAuthMethod === 'storageKey') {
            if (!formData.azureBlobStorageData?.accountName) {
              toast.error('Please enter Storage Account Name');
              setTesting(false);
              return;
            }
            if (!formData.azureBlobStorageData?.accountKey && !actualCredentialData?.accountKey) {
              toast.error('Please enter Account Key');
              setTesting(false);
              return;
            }
          } else if (testAzureAuthMethod === 'connectionString') {
            if (!formData.azureBlobStorageData?.connectionString && !actualCredentialData?.connectionString) {
              toast.error('Please enter Connection String');
              setTesting(false);
              return;
            }
          } else if (testAzureAuthMethod === 'sasToken') {
            if (!formData.azureBlobStorageData?.accountName) {
              toast.error('Please enter Storage Account Name');
              setTesting(false);
              return;
            }
            if (!formData.azureBlobStorageData?.sasToken && !actualCredentialData?.sasToken) {
              toast.error('Please enter SAS Token');
              setTesting(false);
              return;
            }
          }
          testPayload.data = {
            authMethod: testAzureAuthMethod,
            accountName: formData.azureBlobStorageData?.accountName || '',
            accountKey: getActualValue(formData.azureBlobStorageData?.accountKey, actualCredentialData?.accountKey),
            connectionString: getActualValue(formData.azureBlobStorageData?.connectionString, actualCredentialData?.connectionString),
            sasToken: getActualValue(formData.azureBlobStorageData?.sasToken, actualCredentialData?.sasToken),
          };
          break;
        case 'accessDocument':
          if (!formData.accessDocumentData?.instanceId) {
            toast.error('Please enter Instance ID');
            setTesting(false);
            return;
          }
          if (!formData.accessDocumentData?.sharedSecret && !actualCredentialData?.sharedSecret) {
            toast.error('Please enter Shared Secret');
            setTesting(false);
            return;
          }
          testPayload.data = {
            instanceId: formData.accessDocumentData.instanceId,
            sharedSecret: getActualValue(formData.accessDocumentData.sharedSecret, actualCredentialData?.sharedSecret),
            webServiceUrl: formData.accessDocumentData.webServiceUrl || '',
          };
          break;
        case 'donorfy':
          if (!formData.donorfyData?.apiKey && !actualCredentialData?.apiKey) {
            toast.error('Please enter API Key');
            setTesting(false);
            return;
          }
          testPayload.data = {
            apiKey: getActualValue(formData.donorfyData?.apiKey, actualCredentialData?.apiKey),
            username: formData.donorfyData?.username || 'EvoWorkflow',
          };
          break;
        case 'peoplehr':
          if (!formData.peopleHRData?.apiKey && !actualCredentialData?.apiKey) {
            toast.error('Please enter API Key');
            setTesting(false);
            return;
          }
          testPayload.data = {
            apiKey: getActualValue(formData.peopleHRData?.apiKey, actualCredentialData?.apiKey),
          };
          break;
        case 'paycircle':
          if (!formData.paycircleData?.username && !actualCredentialData?.username) {
            toast.error('Please enter Username');
            setTesting(false);
            return;
          }
          if (!formData.paycircleData?.password && !actualCredentialData?.password) {
            toast.error('Please enter Password');
            setTesting(false);
            return;
          }
          testPayload.data = {
            username: getActualValue(formData.paycircleData?.username, actualCredentialData?.username),
            password: getActualValue(formData.paycircleData?.password, actualCredentialData?.password),
            baseUrl: formData.paycircleData?.baseUrl || '',
          };
          break;
        case 'access-charity-crm':
          if (!formData.accessCharityCrmData?.apiKey && !formData.accessCharityCrmData?.bearerToken &&
              !actualCredentialData?.apiKey && !actualCredentialData?.bearerToken) {
            toast.error('Please enter at least one of: API Key or Bearer Token');
            setTesting(false);
            return;
          }
          testPayload.data = {
            apiKey: getActualValue(formData.accessCharityCrmData?.apiKey, actualCredentialData?.apiKey),
            bearerToken: getActualValue(formData.accessCharityCrmData?.bearerToken, actualCredentialData?.bearerToken),
            baseUrl: formData.accessCharityCrmData?.baseUrl || '',
          };
          break;
        case 'microsoft_teams_oauth':
          testPayload.data = {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
          };
          break;
        case 'microsoft_outlook_oauth':
          testPayload.data = {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
          };
          break;
        case 'microsoft_sharepoint_oauth':
          testPayload.data = {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
          };
          break;
        case 'microsoft_onedrive_oauth':
          testPayload.data = {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
          };
          break;
        case 'microsoft_entra_oauth':
          testPayload.data = {
            tenantId: formData.tenantId,
            clientId: formData.clientId,
            clientSecret: getActualValue(formData.clientSecret, actualCredentialData.clientSecret),
          };
          break;
      }

      // Use a direct test endpoint that doesn't require saved credentials
      const response = await credentialsApi.testDirect(testPayload);
      
      // Extract the actual data from axios response
      const result = response.data;
      
      // Check the success field in the response body
      if (result.success === false) {
        // Backend returned success: false
        const errorMessage = result.message || result.error || 'Connection test failed';
        setTestResult({
          success: false,
          message: errorMessage,
          data: result.data,
          details: result.details,
        });
        toast.error(errorMessage);
      } else {
        // Backend returned success: true
        setTestResult({
          success: true,
          message: result.message || 'Connection test successful!',
          data: result.data,
          details: result.details,
        });
        toast.success('Connection test successful!');
      }
    } catch (error: any) {
      // Network error or HTTP error status (4xx, 5xx)
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Connection test failed';
      setTestResult({
        success: false,
        message: errorMessage,
        data: error.response?.data,
      });
      toast.error(errorMessage);
    } finally {
      setTesting(false);
    }
  };

  const openEditDialog = async (credential: Credential) => {
    setEditingCredential(credential);

    // Fetch the full credential data including decrypted fields
    try {
      const response = await credentialsApi.get(credential.id);
      const fullCredential = response.data;
      const data = fullCredential.data || {};

      // Debug logging for FTP credentials
      if (credential.type === 'ftp') {
        console.log('[Credentials] Loading FTP credential:', {
          fullCredential,
          data,
          protocol: data.protocol,
          host: data.host,
          port: data.port,
        });
      }

      setFormData({
        name: credential.name,
        type: credential.type,
        value: MASKED_VALUE,
        username: data.user || data.username || '', // Username/email is plain text, not sensitive
        password: data.password ? MASKED_VALUE : '', // Only mask if password exists
        clientId: isMicrosoftOAuth(credential.type) ? (data.clientId || '') : MASKED_VALUE,
        clientSecret: data.clientSecret ? MASKED_VALUE : '',
        authUrl: data.authUrl || '',
        tokenUrl: data.tokenUrl || '',
        headerName: data.headerName || '',
        host: data.host || data.server || '', // Support both host and server
        port: data.port ? String(data.port) : '',
        database: data.database || '',
        region: data.region || '',
        accessKeyId: data.accessKeyId || '',
        secretAccessKey: MASKED_VALUE,
        projectId: data.projectId || '',
        secure: data.secure !== undefined ? String(data.secure) : 'false',
        from: data.from || '',
        url: data.url || '',
        authToken: data.authToken ? MASKED_VALUE : '',
        // MS SQL Advanced fields
        server: data.server || data.host || '',
        encrypt: data.encrypt !== undefined ? data.encrypt : true,
        domain: data.domain || '',
        connectTimeout: data.connectTimeout ? String(data.connectTimeout) : '',
        requestTimeout: data.requestTimeout ? String(data.requestTimeout) : '',
        trustServerCertificate: data.trustServerCertificate || false,
        tdsVersion: data.tdsVersion || '',
        enableArithAbort: data.enableArithAbort !== undefined ? data.enableArithAbort : true,
        // FTP fields
        ftpData: credential.type === 'ftp' ? {
          protocol: data.protocol || 'ftp',
          host: data.host || '',
          port: data.port || (data.protocol === 'sftp' ? 22 : 21),
          username: data.username || '',
          password: data.password ? MASKED_VALUE : '',
          privateKey: data.privateKey ? MASKED_VALUE : '',
          passphrase: data.passphrase ? MASKED_VALUE : '',
          timeout: data.timeout || 60000,
          ssl: data.ssl || 'none',
          enableSsh: data.enableSsh ?? (data.protocol === 'sftp'),
          sshMode: data.sshMode || 'password',
          passive: data.passive ?? true,
          authType: data.authType || 'credentials',
        } : undefined,
        // Azure Blob Storage fields
        azureBlobStorageData: credential.type === 'azure_blob_storage' ? {
          authMethod: data.authMethod || 'storageKey',
          accountName: data.accountName || '',
          accountKey: data.accountKey ? MASKED_VALUE : '',
          connectionString: data.connectionString ? MASKED_VALUE : '',
          sasToken: data.sasToken ? MASKED_VALUE : '',
        } : undefined,
        // Access Document fields
        accessDocumentData: credential.type === 'accessDocument' ? {
          instanceId: data.instanceId || '',
          sharedSecret: data.sharedSecret ? MASKED_VALUE : '',
          webServiceUrl: data.webServiceUrl || '',
        } : undefined,
        // Donorfy fields
        donorfyData: credential.type === 'donorfy' ? {
          apiKey: data.apiKey ? MASKED_VALUE : '',
          username: data.username || 'EvoWorkflow',
        } : undefined,
        // PeopleHR fields
        peopleHRData: credential.type === 'peoplehr' ? {
          apiKey: data.apiKey ? MASKED_VALUE : '',
        } : undefined,
        // PayCircle fields
        paycircleData: credential.type === 'paycircle' ? {
          username: data.username ? MASKED_VALUE : '',
          password: data.password ? MASKED_VALUE : '',
          baseUrl: data.baseUrl || '',
        } : undefined,
        // Access Charity CRM fields
        accessCharityCrmData: credential.type === 'access-charity-crm' ? {
          apiKey: data.apiKey ? MASKED_VALUE : '',
          bearerToken: data.bearerToken ? MASKED_VALUE : '',
          baseUrl: data.baseUrl || '',
        } : undefined,
        // Microsoft Teams OAuth fields
        tenantId: data.tenantId || '',
        scopes: data.scopes || '',
        accessToken: data.accessToken || '',
        refreshToken: data.refreshToken || '',
        tokenExpiresAt: data.tokenExpiresAt || '',
        tokenType: data.tokenType || '',
        userEmail: data.userEmail || '',
        userName: data.userName || '',
        userId: data.userId || '',
      });
      // Set the correct category for this credential type
      setSelectedCategory(getCategoryForType(credential.type));
    } catch (error) {
      // console.error('Failed to load credential details:', error);
      toast.error('Failed to load credential details');
    }
    
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'api_key',
      value: '',
      username: '',
      password: '',
      clientId: '',
      clientSecret: '',
      authUrl: '',
      tokenUrl: '',
      headerName: '',
      host: '',
      port: '',
      database: '',
      region: '',
      accessKeyId: '',
      secretAccessKey: '',
      projectId: '',
      secure: 'false',
      from: '',
      url: '',
      authToken: '',
      // MS SQL Advanced fields
      server: '',
      encrypt: true,
      domain: '',
      connectTimeout: '',
      requestTimeout: '',
      trustServerCertificate: false,
      tdsVersion: '',
      enableArithAbort: true,
      // FTP fields
      ftpData: undefined,
      // Azure Blob Storage fields
      azureBlobStorageData: undefined,
      // Access Document fields
      accessDocumentData: undefined,
      // Donorfy fields
      donorfyData: undefined,
      // PeopleHR fields
      peopleHRData: undefined,
      // PayCircle fields
      paycircleData: undefined,
      // Access Charity CRM fields
      accessCharityCrmData: undefined,
      // MySQL fields
      mySqlData: undefined,
      // MongoDB fields
      mongoDBData: undefined,
      // PostgreSQL fields
      postgreSqlData: undefined,
    });
    setSelectedCategory('general');
    setTestResult(null);
    setTesting(false);
  };

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection(field === 'updated_at' || field === 'created_at' ? 'desc' : 'asc');
    }
  };

  // Derive owned vs shared credential lists
  const ownedCredentials = useMemo(() =>
    credentials.filter(c => c.isOwner !== false),
    [credentials]
  );
  const sharedCredentials = useMemo(() =>
    credentials.filter(c => c.isSharedWithMe === true),
    [credentials]
  );
  const hasSharedCredentials = sharedCredentials.length > 0;

  // Auto-switch to owned tab if shared tab becomes empty
  useEffect(() => {
    if (activeTab === 'shared' && !hasSharedCredentials) {
      setActiveTab('owned');
    }
  }, [activeTab, hasSharedCredentials]);

  // Filter and sort credentials based on active tab
  const filteredCredentials = useMemo(() => {
    // First filter by active tab
    const tabFiltered = activeTab === 'owned' ? ownedCredentials : sharedCredentials;

    // Then apply search and type filters
    const filtered = tabFiltered.filter(credential => {
      // Search filter
      const matchesSearch = !debouncedSearchQuery ||
        credential.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        getCredentialTypeLabel(credential.type).toLowerCase().includes(debouncedSearchQuery.toLowerCase());

      // Type filter
      const matchesType = !typeFilter || credential.type === typeFilter;

      return matchesSearch && matchesType;
    });

    // Sort the filtered results
    return filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'type':
          aValue = getCredentialTypeLabel(a.type).toLowerCase();
          bValue = getCredentialTypeLabel(b.type).toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at).getTime();
          bValue = new Date(b.updated_at).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [activeTab, ownedCredentials, sharedCredentials, debouncedSearchQuery, typeFilter, sortField, sortDirection]);

  // Reset to page 1 when filters/tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, typeFilter, activeTab, pageSize]);

  // Paginated credentials
  const paginatedCredentials = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredCredentials.slice(startIndex, endIndex);
  }, [filteredCredentials, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredCredentials.length / pageSize));

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
  };

  const hasActiveFilters = searchQuery || typeFilter;

  // Get unique credential types for the filter dropdown
  const credentialTypes = useMemo(() => {
    const types = new Set(credentials.map(c => c.type));
    return Array.from(types).sort();
  }, [credentials]);

  // Sortable header component
  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <TableHead 
        className="cursor-pointer select-none hover:bg-muted/50 transition-colors"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-2">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="w-4 h-4" />
            ) : (
              <ArrowDown className="w-4 h-4" />
            )
          ) : (
            <ArrowUpDown className="w-4 h-4 opacity-30" />
          )}
        </div>
      </TableHead>
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'api_key':
        return <Key className="w-4 h-4" />;
      case 'bearer_token':
        return <Shield className="w-4 h-4" />;
      case 'basic_auth':
        return <Lock className="w-4 h-4" />;
      case 'oauth2':
        return <Code className="w-4 h-4" />;
      case 'api_key_header':
        return <Key className="w-4 h-4" />;
      case 'data-engine':
        return <Database className="w-4 h-4" />;
      case 'claude':
        return <SiAnthropic className="w-4 h-4" color="#D97757" />;
      case 'openai':
        return <SiOpenai className="w-4 h-4" color="#412991" />;
      case 'http_basic':
      case 'http_bearer':
        return <Globe className="w-4 h-4" />;
      case 'msSqlCredential':
        return <SqlServerIcon size={16} />;
      case 'postgresql':
        return <SiPostgresql className="w-4 h-4" color="#4169E1" />;
      case 'mysql':
        return <SiMysql className="w-4 h-4" color="#4479A1" />;
      case 'mongodb':
        return <SiMongodb className="w-4 h-4" color="#47A248" />;
      case 'redis':
        return <SiRedis className="w-4 h-4" color="#DC382D" />;
      case 'turso':
        return <Database className="w-4 h-4" />;
      case 'aws':
        return <Cloud className="w-4 h-4" />; // AWS icon not available in Simple Icons
      case 'azure':
        return <MicrosoftEntraIcon size={16} />;
      case 'gcp':
        return <SiGooglecloud className="w-4 h-4" color="#4285F4" />;
      case 'smtp':
        return <Globe className="w-4 h-4" />;
      case 'ftp':
        return <Server className="w-4 h-4" />;
      case 'azure_blob_storage':
        return <AzureBlobStorageIcon size={16} />;
      case 'accessDocument':
        return <FileText className="w-4 h-4" />;
      case 'donorfy':
        return <Key className="w-4 h-4" />;
      case 'paycircle':
        return <Banknote className="w-4 h-4" />;
      case 'access-charity-crm':
        return <Key className="w-4 h-4" />;
      case 'microsoft_teams_oauth':
      case 'microsoft_teams_oauth2':
        return <MicrosoftTeamsIcon size={16} />;
      case 'microsoft_outlook_oauth':
      case 'microsoft_outlook_oauth2':
        return <MicrosoftOutlookIcon size={16} />;
      case 'microsoft_sharepoint_oauth':
      case 'microsoft_sharepoint_oauth2':
        return <SharePointIcon size={16} />;
      case 'microsoft_onedrive_oauth':
      case 'microsoft_onedrive_oauth2':
        return <OneDriveIcon size={16} />;
      case 'microsoft_entra_oauth':
      case 'microsoft_entra_oauth2':
        return <MicrosoftEntraIcon size={16} />;
      case 'asana_oauth2':
        return <SiAsana className="w-4 h-4" color="#F06A6A" />;
      default:
        return <Key className="w-4 h-4" />;
    }
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'api_key':
        return 'API Key';
      case 'bearer_token':
        return 'Bearer Token';
      case 'basic_auth':
        return 'Basic Auth';
      case 'oauth2':
        return 'OAuth2';
      case 'api_key_header':
        return 'API Key Header';
      case 'data-engine':
        return 'Data Engine';
      case 'accessDocument':
        return 'Access Document';
      case 'donorfy':
        return 'Donorfy';
      case 'paycircle':
        return 'PayCircle';
      case 'access-charity-crm':
        return 'Access Charity CRM';
      case 'claude':
        return 'Claude AI';
      case 'openai':
        return 'OpenAI';
      case 'http_basic':
        return 'HTTP Basic Auth';
      case 'http_bearer':
        return 'HTTP Bearer Token';
      case 'msSqlCredential':
        return 'MS SQL Server';
      case 'postgresql':
        return 'PostgreSQL';
      case 'mysql':
        return 'MySQL';
      case 'mongodb':
        return 'MongoDB';
      case 'redis':
        return 'Redis';
      case 'turso':
        return 'Turso';
      case 'aws':
        return 'AWS';
      case 'azure':
        return 'Microsoft Entra ID';
      case 'gcp':
        return 'Google Cloud';
      case 'smtp':
        return 'SMTP Email';
      case 'ftp':
        return 'FTP / SFTP';
      case 'azure_blob_storage':
        return 'Azure Blob Storage';
      case 'microsoft_teams_oauth':
      case 'microsoft_teams_oauth2':
        return 'Microsoft Teams';
      case 'microsoft_outlook_oauth':
      case 'microsoft_outlook_oauth2':
        return 'Microsoft Outlook';
      case 'microsoft_sharepoint_oauth':
      case 'microsoft_sharepoint_oauth2':
        return 'Microsoft SharePoint';
      case 'microsoft_onedrive_oauth':
      case 'microsoft_onedrive_oauth2':
        return 'Microsoft OneDrive';
      case 'microsoft_entra_oauth':
      case 'microsoft_entra_oauth2':
        return 'Microsoft Entra ID';
      case 'microsoft_excel_oauth2':
        return 'Microsoft Excel';
      case 'asana_oauth2':
        return 'Asana';
      default:
        return type;
    }
  };

  if (loading) {
    return <CredentialsPageSkeleton />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 md:p-6">
      <div className="flex-none flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Credentials</h1>
          <p className="text-muted-foreground mt-1">
            Manage authentication credentials for your workflows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setExportDialogOpen(true)} disabled={ownedCredentials.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => { setEditingCredential(null); setShowDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            New Credential
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      {credentials.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Key className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No credentials yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first credential to authenticate with external services
          </p>
          <Button onClick={() => { setEditingCredential(null); setShowDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Credential
          </Button>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'owned' | 'shared')} className="flex flex-col flex-1 overflow-hidden">
          {/* Search and Filters */}
          <div className="flex-none flex items-center gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search credentials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            <Select value={typeFilter || "all"} onValueChange={(value) => setTypeFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {credentialTypes.map(type => (
                  <SelectItem key={type} value={type}>
                    {getCredentialTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Tabs for My Credentials / Shared with Me */}
          <TabsList className="flex-none mb-4">
            <TabsTrigger value="owned" badge={ownedCredentials.length} badgeVariant="secondary">
              My Credentials
            </TabsTrigger>
            {hasSharedCredentials && (
              <TabsTrigger value="shared" badge={sharedCredentials.length} badgeVariant="secondary">
                Shared with Me
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="owned" className="flex-1 flex flex-col overflow-hidden mt-0">
            {filteredCredentials.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Key className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No credentials found</h3>
                <p className="text-muted-foreground mb-4">
                  {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first credential'}
                </p>
                {hasActiveFilters ? (
                  <Button variant="outline" onClick={handleClearFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                ) : (
                  <Button onClick={() => { setEditingCredential(null); setShowDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Credential
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div data-table-container className="flex-1 overflow-auto">
                  <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHeader field="type">Type</SortableHeader>
                        <SortableHeader field="name">Name</SortableHeader>
                        <SortableHeader field="created_at">Created</SortableHeader>
                        <SortableHeader field="updated_at">Updated</SortableHeader>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCredentials.map((credential) => (
                        <TableRow key={credential.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CredentialIcon type={credential.type} size="sm" />
                              <span>{getCredentialTypeLabel(credential.type)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{credential.name}</TableCell>
                          <TableCell>{formatDate(credential.created_at)}</TableCell>
                          <TableCell>
                            {new Date(credential.created_at) > new Date(credential.updated_at)
                              ? formatDate(credential.created_at)
                              : formatDate(credential.updated_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              {/* Share Button */}
                              <Button
                                variant="ghost"
                                size={(credential.shareCount ?? 0) > 0 ? "sm" : "icon"}
                                onClick={() => {
                                  setCredentialToShare(credential);
                                  setShareDialogOpen(true);
                                }}
                                title={
                                  (credential.shareCount ?? 0) > 0
                                    ? `Shared with: ${credential.sharedWithNames?.join(', ') || 'Unknown'}`
                                    : 'Share credential'
                                }
                                className={(credential.shareCount ?? 0) > 0 ? "gap-1 px-2" : ""}
                              >
                                <Share2 className="h-4 w-4" />
                                {(credential.shareCount ?? 0) > 0 && (
                                  <span className="text-xs text-muted-foreground">
                                    {credential.shareCount}
                                  </span>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(credential)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(credential.id, credential.name)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
                {/* Pagination Footer */}
                <div data-pagination-footer className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredCredentials.length)} of {filteredCredentials.length} credentials
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-medium whitespace-nowrap">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {hasSharedCredentials && (
            <TabsContent value="shared" className="flex-1 flex flex-col overflow-hidden mt-0">
              {filteredCredentials.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Key className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No shared credentials found</h3>
                  <p className="text-muted-foreground mb-4">
                    {hasActiveFilters ? 'Try adjusting your filters' : 'No credentials have been shared with you'}
                  </p>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={handleClearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div data-table-container className="flex-1 overflow-auto">
                    <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableHeader field="type">Type</SortableHeader>
                          <SortableHeader field="name">Name</SortableHeader>
                          <TableHead>Shared By</TableHead>
                          <SortableHeader field="updated_at">Updated</SortableHeader>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedCredentials.map((credential) => (
                          <TableRow key={credential.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CredentialIcon type={credential.type} size="sm" />
                                <span>{getCredentialTypeLabel(credential.type)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{credential.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span>{credential.sharedByDisplayName}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {new Date(credential.created_at) > new Date(credential.updated_at)
                                ? formatDate(credential.created_at)
                                : formatDate(credential.updated_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-2 h-9">
                                <div className="flex items-center gap-1.5 px-2 py-1 text-muted-foreground bg-muted/50 rounded-md text-sm">
                                  <Lock className="h-3.5 w-3.5" />
                                  <span>Read-only</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                  </div>
                  {/* Pagination Footer */}
                  <div data-pagination-footer className="flex-shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2 px-3 py-1.5 bg-card border rounded-lg mt-2">
                    <div className="text-sm text-muted-foreground text-center sm:text-left">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredCredentials.length)} of {filteredCredentials.length} credentials
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm font-medium whitespace-nowrap">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Create/Edit Dialog - Using new CredentialDialog component */}
      <CredentialDialog
        open={showDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowDialog(false);
            setEditingCredential(null);
          }
        }}
        credential={editingCredential}
        onSuccess={() => {
          loadCredentials();
          setShowDialog(false);
          setEditingCredential(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credential</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the credential "{credentialToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setCredentialToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCredential}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Credential Dialog */}
      <ShareCredentialDialog
        credential={credentialToShare}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        onSharesUpdated={loadCredentials}
      />

      {/* Export Credentials Dialog */}
      <ExportCredentialsDialog
        credentialCount={ownedCredentials.length}
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
      />

      {/* Import Credentials Dialog */}
      <ImportCredentialsDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportComplete={loadCredentials}
      />
    </div>
  );
}
